import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { FALLBACK_CONTENT } from '../config';
import { fetchAdminContent, publishAdminContent, deleteAdminContent } from './adminApi';

// ─── Cache en módulo (persiste entre renders sin re-fetch) ───────────────────
let _cache = null;
const SESSION_KEY = 'admin_content_cache';
// Versión del cache — se incrementa al publicar para invalidar tabs cruzados
const CACHE_VERSION_KEY = 'admin_content_version';

export const CONTENT_LANGS = ['es', 'en'];

export function normalizeContentLang(lang) {
    if (!lang) return 'es';
    const l = String(lang).toLowerCase();
    if (l === 'en' || l.startsWith('en-')) return 'en';
    return 'es';
}

function buildSkillSlugs(skills) {
    const slugs = {};
    const slugsReverse = {};
    Object.entries(skills).forEach(([key, skill]) => {
        const slug = skill.slug || key.toLowerCase().replace(/\s+/g, '_');
        slugs[key] = slug;
        slugsReverse[slug] = key;
    });
    return { slugs, slugsReverse };
}

function buildSkillConfigTemplates(criteria, challenges) {
    const templates = {};
    Object.entries(criteria).forEach(([slug, criteriaMap]) => {
        if (!templates[slug]) templates[slug] = { criteria: {}, challenge_templates: [] };
        templates[slug].criteria = criteriaMap;
    });
    Object.entries(challenges).forEach(([slug, list]) => {
        if (!templates[slug]) templates[slug] = { criteria: {}, challenge_templates: [] };
        templates[slug].challenge_templates = list;
    });
    return templates;
}

/** Convierte mapa id → escenario al array que espera el backend. */
export function escenariosMapToArray(map) {
    if (!map || typeof map !== 'object') return [];
    return Object.entries(map).map(([id, v]) => ({ id, ...v }));
}

/** Normaliza respuesta backend (array u objeto) a mapa id → datos (sin id duplicado en valor). */
export function escenariosArrayToMap(arrOrObj) {
    const out = {};
    if (!arrOrObj) return out;
    if (Array.isArray(arrOrObj)) {
        arrOrObj.forEach((item) => {
            const id = item?.id;
            if (!id) return;
            const { id: _id, ...rest } = item;
            out[id] = rest;
        });
        return out;
    }
    return { ...arrOrObj };
}

/** skill_config para POST: criteria + challenges + metadatos de Skills (skill_key, nombre, descripcion). */
function enrichSkillConfigFromParts(skills, criteria, challenges) {
    const base = buildSkillConfigTemplates(criteria, challenges);
    const slugToSkillKey = {};
    Object.entries(skills || {}).forEach(([key, s]) => {
        const slug = (s && s.slug) || key.toLowerCase().replace(/\s+/g, '_');
        slugToSkillKey[slug] = key;
    });
    Object.keys(base).forEach((slug) => {
        const key = slugToSkillKey[slug];
        const s = key ? skills[key] : null;
        if (s) {
            base[slug] = {
                ...base[slug],
                skill_key: key,
                nombre: s.nombre,
                descripcion: s.descripcion || '',
            };
        }
    });
    return base;
}

/** A partir de skill_config del backend → skills, slugs y plantillas solo criterios/desafíos. */
function denormalizeSkillBundle(skillConfigRaw, fallbackSkills) {
    const skillConfig = skillConfigRaw || {};
    const skillConfigTemplates = {};
    const skills = {};
    const slugs = {};
    const slugsReverse = {};

    Object.entries(skillConfig).forEach(([slug, cfg]) => {
        skillConfigTemplates[slug] = {
            criteria: cfg.criteria || {},
            challenge_templates: cfg.challenge_templates || [],
        };
        const skillKey = cfg.skill_key || FALLBACK_CONTENT.skillSlugsReverse[slug] || slug;
        slugsReverse[slug] = skillKey;
        slugs[skillKey] = slug;
        skills[skillKey] = {
            nombre: cfg.nombre != null && cfg.nombre !== '' ? cfg.nombre : (fallbackSkills[skillKey]?.nombre || skillKey),
            slug,
            descripcion: cfg.descripcion != null && cfg.descripcion !== ''
                ? cfg.descripcion
                : (fallbackSkills[skillKey]?.descripcion || ''),
        };
    });

    Object.entries(fallbackSkills || {}).forEach(([key, s]) => {
        if (skills[key]) return;
        const slug = s.slug || key.toLowerCase().replace(/\s+/g, '_');
        // Si el slug ya está cubierto por otro skill_key (ej. 'LEADERSHIP' en el slice EN),
        // no agregar la key del fallback (ej. 'LIDERAZGO') como duplicado.
        if (slugsReverse[slug]) return;
        if (skillConfigTemplates[slug]) {
            skills[key] = { nombre: s.nombre, slug, descripcion: s.descripcion || '' };
            slugs[key] = slug;
            slugsReverse[slug] = key;
        }
    });

    return { skills, skillSlugs: slugs, skillSlugsReverse: slugsReverse, skillConfigTemplates };
}

function extractCriteriaFromFallback() {
    const out = {};
    Object.entries(FALLBACK_CONTENT.skillConfigTemplates).forEach(([slug, cfg]) => {
        out[slug] = cfg.criteria || {};
    });
    return out;
}

function extractChallengesFromFallback() {
    const out = {};
    Object.entries(FALLBACK_CONTENT.skillConfigTemplates).forEach(([slug, cfg]) => {
        out[slug] = cfg.challenge_templates || [];
    });
    return out;
}

function fallbackSkillConfigEnriched() {
    return enrichSkillConfigFromParts(
        FALLBACK_CONTENT.skills,
        extractCriteriaFromFallback(),
        extractChallengesFromFallback(),
    );
}

function sourceFlagsFromBundle(bundle) {
    const escMap = escenariosArrayToMap(bundle?.escenarios);
    const hasEsc = Object.keys(escMap).length > 0;
    const hasSm = bundle?.scene_moments && Object.keys(bundle.scene_moments).length > 0;
    const hasSc = bundle?.skill_config && Object.keys(bundle.skill_config).length > 0;
    return {
        escenarios: hasEsc ? 'backend' : 'fallback',
        scene_moments: hasSm ? 'backend' : 'none',
        skill_config: hasSc ? 'backend' : 'fallback',
    };
}

// Campos que son atributos del personaje, no del idioma: el video y los
// identificadores de voz/agente. Si el admin solo los completó en uno de
// los idiomas, los heredamos al otro para no romper la UI.
const CHARACTER_FIELDS = ['avatar_video_url', 'lemonslice_agent_id', 'voice'];

function crossFillCharacterFields(esMap, enMap) {
    if (!esMap || !enMap) return;
    const ids = new Set([...Object.keys(esMap), ...Object.keys(enMap)]);
    for (const id of ids) {
        const es = esMap[id];
        const en = enMap[id];
        if (!es || !en) continue;
        for (const f of CHARACTER_FIELDS) {
            if (!es[f] && en[f]) es[f] = en[f];
            else if (!en[f] && es[f]) en[f] = es[f];
        }
    }
}

function mergeOneLangFromRemoteBundle(bundle) {
    const fbEsc = FALLBACK_CONTENT.escenarios;
    let escenarios = escenariosArrayToMap(bundle?.escenarios);
    if (!Object.keys(escenarios).length) escenarios = { ...fbEsc };

    const scene_moments = bundle?.scene_moments && Object.keys(bundle.scene_moments).length
        ? bundle.scene_moments
        : {};

    const hasSkillConfig = bundle?.skill_config && Object.keys(bundle.skill_config).length > 0;
    const skill_config = hasSkillConfig ? bundle.skill_config : fallbackSkillConfigEnriched();

    const { skills, skillSlugs, skillSlugsReverse, skillConfigTemplates } = denormalizeSkillBundle(
        skill_config,
        FALLBACK_CONTENT.skills,
    );

    return {
        skills,
        skillSlugs,
        skillSlugsReverse,
        skillConfigTemplates,
        escenarios,
        scene_moments,
    };
}

function legacyFlatToBundle(remote) {
    let escenarios = remote.escenarios;
    if (escenarios && !Array.isArray(escenarios)) {
        escenarios = escenariosMapToArray(escenarios);
    }
    let skill_config = remote.skill_config;
    if (!skill_config || !Object.keys(skill_config).length) {
        if (remote.skills && remote.criteria) {
            skill_config = enrichSkillConfigFromParts(
                remote.skills,
                remote.criteria,
                remote.challenges || {},
            );
        }
    }
    return {
        escenarios: escenarios || [],
        scene_moments: remote.scene_moments || {},
        skill_config: skill_config || null,
    };
}

function legacyRemoteSource(remote) {
    const hasSkills = remote.skills && Object.keys(remote.skills).length > 0;
    const hasCriteria = remote.criteria && Object.keys(remote.criteria).length > 0;
    const hasChallenges = remote.challenges && Object.keys(remote.challenges).length > 0;
    const hasEsc = remote.escenarios && (
        Array.isArray(remote.escenarios) ? remote.escenarios.length > 0 : Object.keys(remote.escenarios).length > 0
    );
    const hasSm = remote.scene_moments && Object.keys(remote.scene_moments).length > 0;
    const hasSc = remote.skill_config && Object.keys(remote.skill_config).length > 0;
    const skillBlock = hasSc || (hasSkills && hasCriteria && hasChallenges);
    return {
        skills: hasSkills ? 'backend' : 'fallback',
        criteria: hasCriteria ? 'backend' : 'fallback',
        challenges: hasChallenges ? 'backend' : 'fallback',
        escenarios: hasEsc ? 'backend' : 'fallback',
        scene_moments: hasSm ? 'backend' : 'none',
        skill_config: skillBlock ? 'backend' : 'fallback',
    };
}

/**
 * Combina GET /management/content (bilingüe o legado) con fallback local.
 *
 * Bilingüe: `remote.es` y `remote.en` vienen del storage/admin con escenarios, scene_moments,
 * skill_config, etc. (shape ya normalizado en backend). Se construye `byLang` con slices listos
 * para UI; la rama visible la elige el cliente según idioma (`App` / `getContentForLangSync`).
 * En la raíz se repite el slice ES para compat (panel admin, código que espera `content.escenarios`).
 */
function mergeWithFallback(remote) {
    const updated_at = remote?.updated_at || null;

    if (remote?.es && typeof remote.es === 'object') {
        const enIn = remote.en && typeof remote.en === 'object' ? remote.en : remote.es;
        const sliceEs = mergeOneLangFromRemoteBundle(remote.es);
        const sliceEn = mergeOneLangFromRemoteBundle(enIn);
        // Fallback cruzado para campos que son del personaje, no del idioma:
        // avatar video, agent_id de Lemon Slice, voice del TTS. Si el admin
        // solo los llenó en un idioma, el otro los hereda — así no se rompe
        // el avatar al cambiar la UI a EN si solo se cargó en ES (o viceversa).
        crossFillCharacterFields(sliceEs.escenarios, sliceEn.escenarios);
        return {
            updated_at,
            isBilingual: true,
            byLang: { es: sliceEs, en: sliceEn },
            ...sliceEs,
            _source: { es: sourceFlagsFromBundle(remote.es), en: sourceFlagsFromBundle(enIn) },
        };
    }

    const bundle = legacyFlatToBundle(remote || {});
    const slice = mergeOneLangFromRemoteBundle(bundle);
    return {
        updated_at,
        isBilingual: false,
        byLang: { es: slice, en: slice },
        ...slice,
        _source: legacyRemoteSource(remote || {}),
    };
}

// ─── Carga desde backend ─────────────────────────────────────────────────────
const _cacheKeyFor = (experience) => {
    if (!experience) return SESSION_KEY;
    return `${SESSION_KEY}:exp:${experience}`;
};

export async function loadContent({ experience } = {}) {
    const key = _cacheKeyFor(experience);
    try {
        const cached = localStorage.getItem(key);
        if (cached) {
            _cache = JSON.parse(cached);
            // Cache pre-existente puede haber sido generado antes del fix de
            // cross-fill bilingüe (avatar_video_url etc. ligados al personaje,
            // no al idioma). Re-aplicamos por idempotencia: solo rellena los
            // huecos, no pisa nada que ya esté.
            if (_cache?.byLang?.es?.escenarios && _cache?.byLang?.en?.escenarios) {
                crossFillCharacterFields(_cache.byLang.es.escenarios, _cache.byLang.en.escenarios);
            }
            return _cache;
        }
    } catch (_) {}

    try {
        const remote = await fetchAdminContent({ experience });
        const merged = mergeWithFallback(remote || {});
        _cache = merged;
        try { localStorage.setItem(key, JSON.stringify(merged)); } catch (_) {}
        return merged;
    } catch (_) {
        const merged = mergeWithFallback({});
        _cache = merged;
        return merged;
    }
}

/** Objeto merge completo (raíz = ES). */
export function getContentSync() {
    if (_cache) return _cache;
    return mergeWithFallback({});
}

/** Slice ya resuelto para simulación / UI según idioma. */
export function getContentForLangSync(lang) {
    const L = normalizeContentLang(lang);
    const base = getContentSync();
    if (base.byLang?.[L]) return base.byLang[L];
    return base;
}

export async function saveContent(parsedJson, { experience } = {}) {
    await publishAdminContent(parsedJson, { experience });
    _cache = null;
    try {
        localStorage.removeItem(_cacheKeyFor(experience));
        // Bump de versión → dispara evento 'storage' en otros tabs para que se refresquen
        localStorage.setItem(CACHE_VERSION_KEY, String(Date.now()));
    } catch (_) {}
    return loadContent({ experience });
}

export async function resetContent({ experience } = {}) {
    await deleteAdminContent({ experience });
    _cache = null;
    try {
        localStorage.removeItem(_cacheKeyFor(experience));
        localStorage.setItem(CACHE_VERSION_KEY, String(Date.now()));
    } catch (_) {}
    return loadContent({ experience });
}

// ─── Excel: columnas ─────────────────────────────────────────────────────────
const LOCALE_SUFFIX = { es: '_ES', en: '_EN' };
const BASE_REQUIRED = ['Skills', 'Criteria', 'Challenges', 'Escenarios'];
const OPTIONAL_BASE = ['SceneMoments'];

const ESCENARIOS_REQUIRED_COLS = ['id', 'nombre', 'tipo_proyecto', 'descripcion_breve', 'descripcion_detallada', 'instrucciones_actuacion'];
const SHEET_COLUMNS = {
    Skills:       ['key', 'slug', 'nombre', 'descripcion'],
    Criteria:     ['skill_slug', 'criterio_key', 'nivel_minimo', 'indicador_1', 'indicador_2', 'indicador_3'],
    Challenges:   ['skill_slug', 'id', 'label', 'nivel', 'template'],
    // hidden_objectives + walk_away_definition son opcionales (solo Arena).
    // Se separan de ESCENARIOS_REQUIRED_COLS para que escenarios ambientales
    // sin esas columnas sigan validando.
    Escenarios:   [...ESCENARIOS_REQUIRED_COLS, 'nombre_personaje', 'cargo_personaje', 'hidden_objectives', 'walk_away_definition', 'relacion_previa', 'lemonslice_agent_id', 'voice', 'avatar_video_url'],
    SceneMoments: ['stage', 'id', 'text', 'triggers', 'next_stage'],
};

// Splitter para hidden_objectives. Formato esperado en cada celda:
//   "label_corto: descripción narrativa | label2: descripción 2 | ..."
// Acepta "|" o ";" como separador entre objetivos. Cada item se parte
// por el PRIMER ":" — antes va el label corto (key), después la
// descripción rica (contexto para el LLM). Si no hay ":", el item
// completo se trata como label sin descripción.
function parseHiddenObjectives(raw) {
    return String(raw || '')
        .split(/[|;]/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(item => {
            const idx = item.indexOf(':');
            if (idx === -1) {
                return { label: item, description: '' };
            }
            return {
                label: item.slice(0, idx).trim(),
                description: item.slice(idx + 1).trim(),
            };
        })
        .filter(o => o.label);
}

function emptySummary() {
    return { skills: 0, criteria: 0, challenges: 0, escenarios: 0, sceneMoments: 0 };
}

function parseOneLocaleBlock(wb, sheetSuffix, errors, summary) {
    const result = { skills: {}, criteria: {}, challenges: {}, escenarios: {}, scene_moments: {} };

    for (const base of BASE_REQUIRED) {
        const sheetName = base + sheetSuffix;
        if (!wb.SheetNames.includes(sheetName)) {
            errors.push(`Falta hoja obligatoria "${sheetName}" (plantilla bilingüe).`);
            continue;
        }
        const ws = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (rows.length === 0) {
            errors.push(`Hoja "${sheetName}" está vacía.`);
            continue;
        }

        const requiredCols = base === 'Escenarios' ? ESCENARIOS_REQUIRED_COLS : SHEET_COLUMNS[base];
        const actualCols = Object.keys(rows[0]);
        const missingCols = requiredCols.filter(c => !actualCols.includes(c));
        if (missingCols.length > 0) {
            errors.push(`Hoja "${sheetName}": columnas faltantes: ${missingCols.join(', ')}`);
            continue;
        }

        if (base === 'Skills') {
            rows.forEach((row, i) => {
                if (!row.key) { errors.push(`${sheetName} fila ${i + 2}: "key" vacío`); return; }
                result.skills[row.key] = { nombre: row.nombre, slug: row.slug, descripcion: row.descripcion };
                summary.skills++;
            });
        }
        if (base === 'Criteria') {
            rows.forEach((row, i) => {
                const lvl = parseInt(row.nivel_minimo, 10);
                if (![1, 2, 3].includes(lvl)) {
                    errors.push(`${sheetName} fila ${i + 2}: nivel_minimo debe ser 1, 2 o 3 (encontrado: "${row.nivel_minimo}")`);
                }
                const slug = row.skill_slug;
                if (!result.criteria[slug]) result.criteria[slug] = {};
                result.criteria[slug][row.criterio_key] = {
                    nivel_minimo: lvl,
                    indicadores: [row.indicador_1, row.indicador_2, row.indicador_3].filter(Boolean),
                };
                summary.criteria++;
            });
        }
        if (base === 'Challenges') {
            rows.forEach((row, i) => {
                const lvl = parseInt(row.nivel, 10);
                if (![1, 2, 3].includes(lvl)) {
                    errors.push(`${sheetName} fila ${i + 2}: nivel debe ser 1, 2 o 3 (encontrado: "${row.nivel}")`);
                }
                const slug = row.skill_slug;
                if (!result.challenges[slug]) result.challenges[slug] = [];
                result.challenges[slug].push({
                    id: row.id,
                    label: row.label,
                    nivel: lvl,
                    template: row.template,
                });
                summary.challenges++;
            });
        }
        if (base === 'Escenarios') {
            rows.forEach((row, i) => {
                if (!row.id) { errors.push(`${sheetName} fila ${i + 2}: "id" vacío`); return; }
                result.escenarios[row.id] = {
                    nombre: row.nombre,
                    tipo_proyecto: row.tipo_proyecto,
                    descripcion_breve: row.descripcion_breve,
                    descripcion_detallada: row.descripcion_detallada,
                    instrucciones_actuacion: row.instrucciones_actuacion,
                    nombre_personaje: row.nombre_personaje || '',
                    cargo_personaje: row.cargo_personaje || '',
                    hidden_objectives: parseHiddenObjectives(row.hidden_objectives),
                    walk_away_definition: row.walk_away_definition || '',
                    relacion_previa: row.relacion_previa || '',
                    lemonslice_agent_id: row.lemonslice_agent_id || '',
                    voice: row.voice || '',
                    avatar_video_url: row.avatar_video_url || '',
                };
                summary.escenarios++;
            });
        }
    }

    const smName = `SceneMoments${sheetSuffix}`;
    if (wb.SheetNames.includes(smName)) {
        const ws = wb.Sheets[smName];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (rows.length > 0) {
            const actualCols = Object.keys(rows[0]);
            const missingCols = SHEET_COLUMNS.SceneMoments.filter(c => !actualCols.includes(c));
            if (missingCols.length > 0) {
                errors.push(`Hoja "${smName}": columnas faltantes: ${missingCols.join(', ')}`);
            } else {
                rows.forEach((row, i) => {
                    if (!row.stage) { errors.push(`${smName} fila ${i + 2}: "stage" vacío`); return; }
                    if (!row.id) { errors.push(`${smName} fila ${i + 2}: "id" vacío`); return; }
                    if (!result.scene_moments[row.stage]) result.scene_moments[row.stage] = [];
                    result.scene_moments[row.stage].push({
                        id: row.id,
                        text: row.text || '',
                        triggers: row.triggers
                            ? String(row.triggers).split(',').map(t => t.trim()).filter(Boolean)
                            : [],
                        next_stage: row.next_stage || null,
                    });
                    summary.sceneMoments++;
                });
            }
        }
    }

    if (Object.keys(result.scene_moments).length === 0) {
        delete result.scene_moments;
    }

    return result;
}

function toBackendPayloadLocale(localeBlock) {
    const sm = localeBlock.scene_moments && Object.keys(localeBlock.scene_moments).length
        ? localeBlock.scene_moments
        : undefined;
    const out = {
        escenarios: escenariosMapToArray(localeBlock.escenarios),
        skill_config: enrichSkillConfigFromParts(
            localeBlock.skills,
            localeBlock.criteria,
            localeBlock.challenges,
        ),
    };
    if (sm) out.scene_moments = sm;
    return out;
}

/** Plantilla legada (una sola lengua): mismas hojas sin sufijo. */
function parseLegacyXlsx(wb, errors, summary) {
    const REQUIRED_SHEETS = BASE_REQUIRED;
    const OPTIONAL_SHEETS = OPTIONAL_BASE;
    const presentRequired = REQUIRED_SHEETS.filter(s => wb.SheetNames.includes(s));
    const missingSheets = REQUIRED_SHEETS.filter(s => !wb.SheetNames.includes(s));
    if (missingSheets.length > 0) {
        errors.push(`Hojas faltantes: ${missingSheets.join(', ')}.`);
    }
    const presentOptional = OPTIONAL_SHEETS.filter(s => wb.SheetNames.includes(s));
    const allPresent = [...presentRequired, ...presentOptional];
    const result = { skills: {}, criteria: {}, challenges: {}, escenarios: {}, scene_moments: {} };

    for (const sheetName of allPresent) {
        const ws = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (rows.length === 0) {
            if (REQUIRED_SHEETS.includes(sheetName)) {
                errors.push(`Hoja "${sheetName}" está vacía — se usará el fallback.`);
            }
            continue;
        }
        const baseName = sheetName;
        const requiredCols = baseName === 'Escenarios' ? ESCENARIOS_REQUIRED_COLS : SHEET_COLUMNS[baseName];
        const actualCols = Object.keys(rows[0]);
        const missingCols = requiredCols.filter(c => !actualCols.includes(c));
        if (missingCols.length > 0) {
            errors.push(`Hoja "${sheetName}": columnas faltantes: ${missingCols.join(', ')}`);
            continue;
        }
        if (sheetName === 'Skills') {
            rows.forEach((row, i) => {
                if (!row.key) { errors.push(`Skills fila ${i + 2}: "key" vacío`); return; }
                result.skills[row.key] = { nombre: row.nombre, slug: row.slug, descripcion: row.descripcion };
                summary.skills++;
            });
        }
        if (sheetName === 'Criteria') {
            rows.forEach((row, i) => {
                const lvl = parseInt(row.nivel_minimo, 10);
                if (![1, 2, 3].includes(lvl)) {
                    errors.push(`Criteria fila ${i + 2}: nivel_minimo debe ser 1, 2 o 3 (encontrado: "${row.nivel_minimo}")`);
                }
                const slug = row.skill_slug;
                if (!result.criteria[slug]) result.criteria[slug] = {};
                result.criteria[slug][row.criterio_key] = {
                    nivel_minimo: lvl,
                    indicadores: [row.indicador_1, row.indicador_2, row.indicador_3].filter(Boolean),
                };
                summary.criteria++;
            });
        }
        if (sheetName === 'Challenges') {
            rows.forEach((row, i) => {
                const lvl = parseInt(row.nivel, 10);
                if (![1, 2, 3].includes(lvl)) {
                    errors.push(`Challenges fila ${i + 2}: nivel debe ser 1, 2 o 3 (encontrado: "${row.nivel}")`);
                }
                const slug = row.skill_slug;
                if (!result.challenges[slug]) result.challenges[slug] = [];
                result.challenges[slug].push({
                    id: row.id,
                    label: row.label,
                    nivel: lvl,
                    template: row.template,
                });
                summary.challenges++;
            });
        }
        if (sheetName === 'Escenarios') {
            rows.forEach((row, i) => {
                if (!row.id) { errors.push(`Escenarios fila ${i + 2}: "id" vacío`); return; }
                result.escenarios[row.id] = {
                    nombre: row.nombre,
                    tipo_proyecto: row.tipo_proyecto,
                    descripcion_breve: row.descripcion_breve,
                    descripcion_detallada: row.descripcion_detallada,
                    instrucciones_actuacion: row.instrucciones_actuacion,
                    nombre_personaje: row.nombre_personaje || '',
                    cargo_personaje: row.cargo_personaje || '',
                    hidden_objectives: parseHiddenObjectives(row.hidden_objectives),
                    walk_away_definition: row.walk_away_definition || '',
                    relacion_previa: row.relacion_previa || '',
                    lemonslice_agent_id: row.lemonslice_agent_id || '',
                    voice: row.voice || '',
                    avatar_video_url: row.avatar_video_url || '',
                };
                summary.escenarios++;
            });
        }
        if (sheetName === 'SceneMoments') {
            rows.forEach((row, i) => {
                if (!row.stage) { errors.push(`SceneMoments fila ${i + 2}: "stage" vacío`); return; }
                if (!row.id) { errors.push(`SceneMoments fila ${i + 2}: "id" vacío`); return; }
                if (!result.scene_moments[row.stage]) result.scene_moments[row.stage] = [];
                result.scene_moments[row.stage].push({
                    id: row.id,
                    text: row.text || '',
                    triggers: row.triggers
                        ? String(row.triggers).split(',').map(t => t.trim()).filter(Boolean)
                        : [],
                    next_stage: row.next_stage || null,
                });
                summary.sceneMoments++;
            });
        }
    }
    if (Object.keys(result.scene_moments).length === 0) {
        delete result.scene_moments;
    }
    return result;
}

export async function parseXlsx(file) {
    const errors = [];
    const arrayBuffer = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuffer, { type: 'array' });

    const bilingual = wb.SheetNames.includes('Skills_ES') && wb.SheetNames.includes('Skills_EN');

    if (bilingual) {
        const summaryEs = emptySummary();
        const summaryEn = emptySummary();
        const blockEs = parseOneLocaleBlock(wb, LOCALE_SUFFIX.es, errors, summaryEs);
        const blockEn = parseOneLocaleBlock(wb, LOCALE_SUFFIX.en, errors, summaryEn);
        const result = {
            es: toBackendPayloadLocale(blockEs),
            en: toBackendPayloadLocale(blockEn),
        };
        const hasBlockingErrors = errors.some(e =>
            e.includes('columnas faltantes') ||
            e.includes('"key" vacío') ||
            e.includes('"id" vacío') ||
            e.includes('Falta hoja obligatoria'),
        );
        const summary = {
            es: summaryEs,
            en: summaryEn,
            skills: summaryEs.skills + summaryEn.skills,
            criteria: summaryEs.criteria + summaryEn.criteria,
            challenges: summaryEs.challenges + summaryEn.challenges,
            escenarios: summaryEs.escenarios + summaryEn.escenarios,
            sceneMoments: summaryEs.sceneMoments + summaryEn.sceneMoments,
        };
        return { result, errors, summary, hasBlockingErrors, bilingual: true };
    }

    const summary = emptySummary();
    const legacy = parseLegacyXlsx(wb, errors, summary);
    const payload = {
        es: toBackendPayloadLocale(legacy),
        en: toBackendPayloadLocale(legacy),
    };
    if (!errors.some(e => e.includes('Hojas faltantes'))) {
        errors.push('Archivo en formato legado (una lengua): se duplicará el mismo contenido en `es` y `en` del backend. Preferir plantilla con hojas *_ES / *_EN.');
    }
    const hasBlockingErrors = errors.some(e =>
        e.includes('columnas faltantes') ||
        e.includes('"key" vacío') ||
        e.includes('"id" vacío'),
    );
    return { result: payload, errors, summary, hasBlockingErrors, bilingual: false };
}

// ─── Generación de .xlsx ──────────────────────────────────────────────────────
function triggerDownload(wb, filename) {
    XLSX.writeFile(wb, filename);
}

function exampleRowSceneMoments(lang) {
    if (lang === 'en') {
        return [
            ['entrada', 'saludo_entrada', 'They greet at the entrance and {character_name} invites them in.', 'saludo_inicial,presentacion_completa', 'conversacion_inicial'],
            ['conversacion_inicial', 'acomodo_sala', '{character_name} lays the project plans on the table.', 'menciona_agenda,propone_plan', ''],
            ['recorrido', 'inicio_recorrido', 'They start the site walkthrough.', 'propone_recorrido,acepta_recorrido', 'inspeccion'],
            ['inspeccion', 'observacion_zona', 'They stop at the storage area to review controls.', 'pregunta_controles,solicita_evidencia', 'cierre'],
            ['cierre', 'regreso_oficina', 'They return to the office to review agreements.', 'propone_cierre,resume_hallazgos', ''],
        ];
    }
    return [
        ['entrada', 'saludo_entrada', 'Se saludan en la entrada y {character_name} los invita a pasar.', 'saludo_inicial,presentacion_completa', 'conversacion_inicial'],
        ['conversacion_inicial', 'acomodo_sala', '{character_name} extiende sobre la mesa los planos del proyecto.', 'menciona_agenda,propone_plan', ''],
        ['recorrido', 'inicio_recorrido', 'Se levantan e inician el recorrido por las instalaciones.', 'propone_recorrido,acepta_recorrido', 'inspeccion'],
        ['inspeccion', 'observacion_zona', 'Se detienen en la zona de almacenamiento para revisar los controles.', 'pregunta_controles,solicita_evidencia', 'cierre'],
        ['cierre', 'regreso_oficina', 'Regresan a la oficina para revisar los acuerdos.', 'propone_cierre,resume_hallazgos', ''],
    ];
}

function appendLocaleSheetsToWorkbook(wb, slice, suffix) {
    const skillRows = [SHEET_COLUMNS.Skills];
    Object.entries(slice.skills || {}).forEach(([key, s]) => {
        skillRows.push([key, slice.skillSlugs[key] || s.slug || key.toLowerCase(), s.nombre || key, s.descripcion || '']);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(skillRows), `Skills${suffix}`);

    const criteriaRows = [SHEET_COLUMNS.Criteria];
    Object.entries(slice.skillConfigTemplates || {}).forEach(([slug, cfg]) => {
        Object.entries(cfg.criteria || {}).forEach(([ckey, crit]) => {
            const inds = crit.indicadores || [];
            criteriaRows.push([slug, ckey, crit.nivel_minimo, inds[0] || '', inds[1] || '', inds[2] || '']);
        });
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(criteriaRows), `Criteria${suffix}`);

    const challengeRows = [SHEET_COLUMNS.Challenges];
    Object.entries(slice.skillConfigTemplates || {}).forEach(([slug, cfg]) => {
        (cfg.challenge_templates || []).forEach(ch => {
            challengeRows.push([slug, ch.id, ch.label, ch.nivel, ch.template]);
        });
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(challengeRows), `Challenges${suffix}`);

    const escRows = [SHEET_COLUMNS.Escenarios];
    Object.entries(slice.escenarios || {}).forEach(([id, esc]) => {
        escRows.push([
            id,
            esc.nombre,
            esc.tipo_proyecto,
            esc.descripcion_breve,
            esc.descripcion_detallada,
            esc.instrucciones_actuacion,
            esc.nombre_personaje ?? esc.interlocutor_nombre ?? '',
            esc.cargo_personaje ?? esc.interlocutor_rol ?? '',
            Array.isArray(esc.hidden_objectives)
                ? esc.hidden_objectives
                    .map(o => {
                        if (typeof o === 'string') return o;
                        if (!o || !o.label) return '';
                        return o.description ? `${o.label}: ${o.description}` : o.label;
                    })
                    .filter(Boolean)
                    .join(' | ')
                : '',
            esc.walk_away_definition ?? '',
            esc.relacion_previa ?? '',
            esc.lemonslice_agent_id ?? '',
            esc.voice ?? '',
            esc.avatar_video_url ?? '',
        ]);
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(escRows), `Escenarios${suffix}`);

    if (slice.scene_moments && Object.keys(slice.scene_moments).length > 0) {
        const smRows = [SHEET_COLUMNS.SceneMoments];
        Object.entries(slice.scene_moments).forEach(([stage, moments]) => {
            (moments || []).forEach(m => {
                smRows.push([
                    stage,
                    m.id || '',
                    m.text || '',
                    Array.isArray(m.triggers) ? m.triggers.join(',') : (m.triggers || ''),
                    m.next_stage || '',
                ]);
            });
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(smRows), `SceneMoments${suffix}`);
    }
}

export function downloadEmptyTemplate() {
    const wb = XLSX.utils.book_new();
    const esSkills = [
        SHEET_COLUMNS.Skills,
        ['NUEVA_HABILIDAD', 'nueva_habilidad', 'Nombre visible (ES)', 'Definición en español…'],
    ];
    const enSkills = [
        SHEET_COLUMNS.Skills,
        ['NUEVA_HABILIDAD', 'nueva_habilidad', 'Display name (EN)', 'Definition in English…'],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(esSkills), 'Skills_ES');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(enSkills), 'Skills_EN');

    const esCrit = [SHEET_COLUMNS.Criteria, ['nueva_habilidad', 'criterio_ejemplo', 2, 'Indicador 1 (ES)', 'Indicador 2', 'Indicador 3']];
    const enCrit = [SHEET_COLUMNS.Criteria, ['nueva_habilidad', 'criterio_ejemplo', 2, 'Indicator 1 (EN)', 'Indicator 2', 'Indicator 3']];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(esCrit), 'Criteria_ES');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(enCrit), 'Criteria_EN');

    const esCh = [SHEET_COLUMNS.Challenges, ['nueva_habilidad', 'challenge_ejemplo', 'Etiqueta (ES)', 2, 'Plantilla del desafío en español…']];
    const enCh = [SHEET_COLUMNS.Challenges, ['nueva_habilidad', 'challenge_ejemplo', 'Label (EN)', 2, 'Challenge template in English…']];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(esCh), 'Challenges_ES');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(enCh), 'Challenges_EN');

    const esEsc = [SHEET_COLUMNS.Escenarios, [
        'id_escenario',
        'Nombre del escenario (ES)',
        'Tipo de proyecto (ES)',
        'Descripción breve',
        'Descripción detallada',
        'Instrucciones simulador (ES)',
        'Nombre personaje',
        'Cargo',
        'objetivo oculto 1 | objetivo oculto 2 | objetivo oculto 3 (solo Arena, dejar vacío en ambiental)',
        'Cuándo se iría de la mesa la contraparte (solo Arena, dejar vacío en ambiental)',
        'Relación previa con el usuario — vacío = no se conocen, ej: "Ya se conocen, han trabajado juntos antes" (opcional, solo Arena)',
        'agent_id de Lemon Slice (opcional — vacío = usa default del backend)',
        'Voz OpenAI: alloy, ash, ballad, coral, echo, fable, nova, onyx, sage, shimmer, verse, cedar, marin (opcional — vacío = cedar)',
        'URL del video del avatar para modo "doblaje" (opcional — solo se usa si REACT_APP_VIDEO_AVATAR_ENABLED=true)',
    ]];
    const enEsc = [SHEET_COLUMNS.Escenarios, [
        'id_escenario',
        'Scenario title (EN)',
        'Project type (EN)',
        'Short description',
        'Detailed description',
        'Simulator instructions (EN)',
        'Character name',
        'Role',
        'hidden objective 1 | hidden objective 2 | hidden objective 3 (Arena only, leave blank for ambiental)',
        'When the counterpart would walk away (Arena only, leave blank for ambiental)',
        'Prior relationship with user — blank = strangers, e.g. "They already know each other from prior work" (optional, Arena only)',
        'Lemon Slice agent_id (optional — blank = backend default)',
        'OpenAI voice: alloy, ash, ballad, coral, echo, fable, nova, onyx, sage, shimmer, verse, cedar, marin (optional — blank = cedar)',
        'Video URL for "dubbing" avatar mode (optional — used only when REACT_APP_VIDEO_AVATAR_ENABLED=true)',
    ]];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(esEsc), 'Escenarios_ES');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(enEsc), 'Escenarios_EN');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([SHEET_COLUMNS.SceneMoments, ...exampleRowSceneMoments('es')]), 'SceneMoments_ES');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([SHEET_COLUMNS.SceneMoments, ...exampleRowSceneMoments('en')]), 'SceneMoments_EN');

    triggerDownload(wb, 'plantilla-simulador-bilingue.xlsx');
}

export function exportCurrentAsXlsx() {
    const content = getContentSync();
    const wb = XLSX.utils.book_new();
    if (content.byLang?.es && content.byLang?.en) {
        appendLocaleSheetsToWorkbook(wb, content.byLang.es, LOCALE_SUFFIX.es);
        appendLocaleSheetsToWorkbook(wb, content.byLang.en, LOCALE_SUFFIX.en);
        triggerDownload(wb, `contenido-simulador-${new Date().toISOString().slice(0, 10)}.xlsx`);
        return;
    }
    appendLocaleSheetsToWorkbook(wb, content, '_ES');
    appendLocaleSheetsToWorkbook(wb, content, '_EN');
    triggerDownload(wb, `contenido-simulador-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── ContentContext ───────────────────────────────────────────────────────────
export const ContentContext = createContext(null);

export function ContentProvider({ children, experience }) {
    const [content, setContent] = useState(null);
    const [isLoadingContent, setIsLoadingContent] = useState(true);
    // Identidad estable: si el caller pasa undefined/null, usamos '' que mapea
    // al blob default ('content.json' / experiencia 'ambiental').
    const expKey = (experience || '').trim();

    const refresh = useCallback(async () => {
        setIsLoadingContent(true);
        _cache = null;
        try { localStorage.removeItem(_cacheKeyFor(expKey)); } catch (_) {}
        const c = await loadContent({ experience: expKey });
        setContent(c);
        setIsLoadingContent(false);
        return c;
    }, [expKey]);

    useEffect(() => {
        setIsLoadingContent(true);
        loadContent({ experience: expKey })
            .then(c => { setContent(c); _cache = c; })
            .finally(() => setIsLoadingContent(false));
    }, [expKey]);

    // Detecta publicaciones desde otros tabs (admin en otra pestaña)
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === CACHE_VERSION_KEY) refresh();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [refresh]);

    return (
        <ContentContext.Provider value={{ content, isLoadingContent, refresh, experience: expKey }}>
            {children}
        </ContentContext.Provider>
    );
}

export function useContent() {
    const ctx = useContext(ContentContext);
    if (!ctx) throw new Error('useContent debe usarse dentro de <ContentProvider>');
    return ctx;
}
